import {
  CheckOutlined,
  DownOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

import type { OrganizationResponse } from '@repo/contracts/tenant-api';

import { useGetAllOrganizationsQuery } from '@/features/organizations/api/organizations.api';
import { useIsTenantOwner } from '@/hooks/use-is-tenant-owner';
import { useProgressRouter } from '@/hooks/use-progress-router';
import { useRole } from '@/hooks/use-role';
import { useScope } from '@/hooks/use-scope';

interface ContextSwitcherProps {
  isCollapsed: boolean;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function ContextSwitcher({ isCollapsed }: ContextSwitcherProps) {
  const { t } = useTranslation();
  const router = useProgressRouter();
  const isTenantOwner = useIsTenantOwner();
  const currentRole = useRole();
  const { organizationSlug } = useScope();
  const { data: organizations, isLoading } = useGetAllOrganizationsQuery();

  const currentOrganization = organizations?.find(
    (org) => org.slug === organizationSlug,
  );

  const isAdminContext = currentRole === 'tenant';
  const isOrgContext = currentRole === 'org' && !!organizationSlug;

  const currentName = isAdminContext
    ? t('navigation.administration')
    : (currentOrganization?.name ?? t('navigation.selectWorkspace'));

  const subtitle = isAdminContext
    ? t('navigation.settingsAndUsers')
    : t('navigation.operations');

  const initials = isAdminContext
    ? undefined
    : currentOrganization?.name
      ? getInitials(currentOrganization.name)
      : '?';

  const menuItems: MenuProps['items'] = [];

  // Organizations group
  if (!isLoading && organizations && organizations.length > 0) {
    menuItems.push({
      type: 'group',
      label: (
        <Typography.Text
          type="secondary"
          className="text-xs font-semibold uppercase"
        >
          {t('navigation.organizations')}
        </Typography.Text>
      ),
      children: organizations.map((org: OrganizationResponse) => {
        const isActive = isOrgContext && org.slug === organizationSlug;

        return {
          key: `org-${org.id}`,
          label: (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar size={24} style={{ fontSize: 11 }}>
                  {getInitials(org.name)}
                </Avatar>
                <span>{org.name}</span>
              </div>
              {isActive && <CheckOutlined className="text-blue-500" />}
            </div>
          ),
          onClick: () => router.push(`/org/${org.slug}`),
        };
      }),
    });
  }

  // Administration option (for tenant owners)
  if (isTenantOwner) {
    menuItems.push({ type: 'divider' });
    menuItems.push({
      key: 'administration',
      label: (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Avatar
              size={24}
              icon={<SettingOutlined />}
              className="bg-gray-500"
              style={{ fontSize: 12 }}
            />
            <div>
              <div>{t('navigation.administration')}</div>
              <Typography.Text type="secondary" className="text-xs">
                {t('navigation.settingsAndUsers')}
              </Typography.Text>
            </div>
          </div>
          {isAdminContext && <CheckOutlined className="text-blue-500" />}
        </div>
      ),
      onClick: () => router.push('/tenant'),
    });
  }

  // Collapsed state: show only avatar/icon
  if (isCollapsed) {
    return (
      <div className="px-2 pb-2">
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          placement="bottomLeft"
        >
          <button className="flex w-full cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800">
            {isAdminContext ? (
              <Avatar
                size={32}
                icon={<SettingOutlined />}
                className="bg-gray-500"
              />
            ) : (
              <Avatar size={32} className="shrink-0 bg-purple-700">
                {initials}
              </Avatar>
            )}
          </button>
        </Dropdown>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3">
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomLeft"
      >
        <button className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-2.5 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
          {isAdminContext ? (
            <Avatar
              size={36}
              icon={<SettingOutlined />}
              className="shrink-0 bg-gray-500"
            />
          ) : (
            <Avatar size={36} className="shrink-0 bg-purple-700">
              {initials}
            </Avatar>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
              {currentName}
            </div>
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">
              {subtitle}
            </div>
          </div>
          <DownOutlined className="text-xs text-gray-400 dark:text-gray-500" />
        </button>
      </Dropdown>
    </div>
  );
}
